import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import axios from 'axios';
import Modal from 'react-modal';
import { Button } from 'react-bootstrap';
import * as Constants from '../Constants';
import { BP_ACTION_ITEMS, bpActionListModalStyle, bpActionOverlayModalStyle } from '../tables/bpActions';

function normalizeRoutes(raw) {
  if (!raw) {
    return [];
  }
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const userType = localStorage.getItem('UserType');
    if (parsed && userType && Array.isArray(parsed[userType])) {
      return parsed[userType];
    }
    if (Array.isArray(parsed)) {
      return parsed;
    }
  } catch (e) {
    return [];
  }
  return [];
}

function isLoggedIn() {
  return !!localStorage.getItem('UserId');
}

function isAdminUser() {
  const t = (localStorage.getItem('UserType') || '').toLowerCase();
  return t === 'admin';
}

function isItemAllowed(item, allowedRoutes) {
  if (item.adminOnly && !isAdminUser()) {
    return false;
  }
  if (item.kind === 'api') {
    return isLoggedIn();
  }
  if (!allowedRoutes || allowedRoutes.length === 0) {
    return false;
  }
  if (item.route && allowedRoutes.indexOf(item.route) >= 0) {
    return true;
  }
  if (item.requiredRoute) {
    return allowedRoutes.some(function (r) {
      return r.indexOf(item.requiredRoute) === 0;
    });
  }
  if (item.route) {
    return allowedRoutes.some(function (r) {
      return r.indexOf(item.route + '/') === 0;
    });
  }
  return false;
}

function itemSearchText(item) {
  return [
    item.title,
    item.menu,
    item.category,
    item.route,
    item.phpFile,
    item.phpPath,
    item.apiPath,
    item.apiMethod,
    (item.keywords || []).join(' ')
  ].filter(Boolean).join(' ').toLowerCase();
}

function scoreItem(item, query) {
  const q = query.toLowerCase().trim();
  if (!q) {
    return 0;
  }
  const title = (item.title || '').toLowerCase();
  const file = (item.phpFile || '').toLowerCase();
  const route = (item.route || '').toLowerCase();
  let score = 0;
  if (title === q) score += 100;
  if (title.indexOf(q) === 0) score += 50;
  if (title.indexOf(q) >= 0) score += 30;
  if (file.indexOf(q) >= 0) score += 25;
  if (route.indexOf(q) >= 0) score += 20;
  if (itemSearchText(item).indexOf(q) >= 0) score += 10;
  return score;
}

function isNumericQuery(query) {
  return /^\d+$/.test((query || '').trim());
}

function shouldRunBpSearch(query) {
  const trimmed = (query || '').trim();
  if (trimmed.length < 2) {
    return false;
  }
  if (isNumericQuery(trimmed)) {
    return trimmed.length >= 10;
  }
  return trimmed.length >= 3;
}

function canSubmitBpSearch(query) {
  const trimmed = (query || '').trim();
  if (trimmed.length < 2) {
    return false;
  }
  if (isNumericQuery(trimmed)) {
    return trimmed.length >= 8;
  }
  return trimmed.length >= 3;
}

function bpSearchDelay(query) {
  return isNumericQuery(query) ? 700 : 350;
}

function passbookPathForBp(bpId) {
  return '/reports/passbook.php?bn=' + bpId;
}

class NavbarSearch extends Component {
  state = {
    query: '',
    results: [],
    open: false,
    activeIndex: 0,
    loaded: false,
    bpOverlayOpen: false,
    bpResults: [],
    bpLoading: false,
    bpMessage: '',
    passbookOverlayOpen: false,
    passbookOverlaySrc: '',
    passbookOverlayTitle: '',
    actionListOpen: false,
    actionListBp: null,
    actionOverlayOpen: false,
    actionOverlayTitle: '',
    actionOverlayPost: false,
    actionOverlayPostAction: '',
    actionOverlayPostField: '',
    actionOverlayUrl: '',
    actionOverlayBn: ''
  };

  containerRef = null;
  bpSearchTimer = null;
  bpSearchCancelSource = null;
  actionFormRef = React.createRef();

  componentDidMount() {
    this.loadIndex();
    document.addEventListener('mousedown', this.handleDocumentMouseDown);
    document.addEventListener('keydown', this.handleDocumentKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleDocumentMouseDown);
    document.removeEventListener('keydown', this.handleDocumentKeyDown);
    if (this.bpSearchTimer) {
      clearTimeout(this.bpSearchTimer);
    }
    if (this.bpSearchCancelSource) {
      this.bpSearchCancelSource.cancel('Navbar search superseded');
      this.bpSearchCancelSource = null;
    }
  }

  handleDocumentKeyDown = (e) => {
    if (e.key === 'Escape' && this.state.actionOverlayOpen) {
      this.closeActionOverlay();
      return;
    }
    if (e.key === 'Escape' && this.state.actionListOpen) {
      this.closeActionList();
      return;
    }
    if (e.key === 'Escape' && this.state.passbookOverlayOpen) {
      this.closePassbookOverlay();
      return;
    }
    if (e.key === 'Escape' && this.state.bpOverlayOpen) {
      this.closeBpOverlay();
    }
  };

  handleDocumentMouseDown = (e) => {
    if (this.containerRef && !this.containerRef.contains(e.target)) {
      this.setState({ open: false });
    }
  };

  loadIndex = () => {
    fetch(process.env.PUBLIC_URL + '/crm-nav-search.json', { cache: 'no-store' })
      .then((res) => res.json())
      .then((data) => {
        this.indexItems = (data && data.items) ? data.items : [];
        this.setState({ loaded: true });
      })
      .catch(() => {
        this.indexItems = [];
        this.setState({ loaded: true });
      });
  };

  getAllowedRoutes = () => {
    return normalizeRoutes(localStorage.getItem('routes'));
  };

  searchNav = (query) => {
    const allowedRoutes = this.getAllowedRoutes();
    let items = (this.indexItems || []).filter((item) => isItemAllowed(item, allowedRoutes));
    if (isNumericQuery(query)) {
      items = items.filter((item) => item.kind !== 'api');
    }
    if (!query || !query.trim()) {
      return [];
    }
    return items
      .map((item) => ({ item, score: scoreItem(item, query) }))
      .filter((row) => row.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 25)
      .map((row) => row.item);
  };

  navigateTo = (item) => {
    if (!item) {
      return;
    }
    if (item.kind === 'api') {
      this.setState({ query: '', results: [], open: false, activeIndex: 0 });
      return;
    }
    if (item.route) {
      this.props.history.push(item.route);
    } else if (item.phpPath) {
      this.props.history.push('/iframe?path=' + encodeURIComponent(item.phpPath));
    }
    this.setState({ query: '', results: [], open: false, activeIndex: 0 });
  };

  closeBpOverlay = () => {
    this.setState({
      bpOverlayOpen: false,
      bpResults: [],
      bpLoading: false,
      bpMessage: '',
      actionListOpen: false,
      actionListBp: null
    });
  };

  openActionList = (bp) => {
    this.setState({ actionListOpen: true, actionListBp: bp });
  };

  closeActionList = () => {
    this.setState({ actionListOpen: false, actionListBp: null });
  };

  closeActionOverlay = () => {
    this.setState({
      actionOverlayOpen: false,
      actionOverlayUrl: '',
      actionOverlayPost: false,
      actionOverlayPostAction: '',
      actionOverlayPostField: '',
      actionOverlayBn: ''
    });
  };

  runBpAction = (item) => {
    const bp = this.state.actionListBp;
    const bn = bp && bp.businessPartnerId;
    const title = item.label + (bn ? ' — BP ' + bn : '');

    if (item.post) {
      this.setState({
        actionListOpen: false,
        actionOverlayOpen: true,
        actionOverlayTitle: title,
        actionOverlayPost: true,
        actionOverlayPostAction: Constants.CS_PORTAL_BASE + item.action,
        actionOverlayPostField: item.field,
        actionOverlayBn: bn
      }, () => {
        if (this.actionFormRef.current) {
          this.actionFormRef.current.submit();
        }
      });
    } else {
      this.setState({
        actionListOpen: false,
        actionOverlayOpen: true,
        actionOverlayTitle: title,
        actionOverlayPost: false,
        actionOverlayUrl: Constants.csPortalIframeSrc(item.path(bn))
      });
    }
  };

  closePassbookOverlay = () => {
    this.setState({
      passbookOverlayOpen: false,
      passbookOverlaySrc: '',
      passbookOverlayTitle: ''
    });
  };

  openPassbookOverlay = (bpId) => {
    const path = passbookPathForBp(bpId);
    this.closeBpOverlay();
    this.setState({
      query: '',
      results: [],
      open: false,
      activeIndex: 0,
      passbookOverlayOpen: true,
      passbookOverlaySrc: Constants.csPortalIframeSrc(path),
      passbookOverlayTitle: 'BP Passbook #' + bpId
    });
  };

  searchBusinessPartners = (query) => {
    if (!isLoggedIn()) {
      return;
    }
    if (this.bpSearchCancelSource) {
      this.bpSearchCancelSource.cancel('Navbar search superseded');
    }
    const cancelSource = axios.CancelToken.source();
    this.bpSearchCancelSource = cancelSource;
    this.setState({ bpLoading: true, bpOverlayOpen: true, bpResults: [], bpMessage: '' });
    axios.get(
      Constants.BACKEND_URL + '/bp/search?searchVal=' + encodeURIComponent(query),
      { timeout: 20000, cancelToken: cancelSource.token }
    )
      .then((res) => {
        if (this.bpSearchCancelSource !== cancelSource) {
          return;
        }
        this.bpSearchCancelSource = null;
        const list = Array.isArray(res.data) ? res.data : [];
        if (list.length === 0) {
          this.setState({
            bpLoading: false,
            bpOverlayOpen: true,
            bpResults: [],
            bpMessage: 'No business partner found for "' + query + '".'
          });
        } else {
          this.setState({
            bpLoading: false,
            bpOverlayOpen: true,
            bpResults: list,
            bpMessage: ''
          });
        }
      })
      .catch((err) => {
        if (this.bpSearchCancelSource !== cancelSource) {
          return;
        }
        this.bpSearchCancelSource = null;
        if (axios.isCancel(err)) {
          return;
        }
        const timedOut = err && err.code === 'ECONNABORTED';
        this.setState({
          bpLoading: false,
          bpOverlayOpen: true,
          bpResults: [],
          bpMessage: timedOut
            ? 'Business partner search timed out. Try again in a moment.'
            : 'Business partner search failed. Try again.'
        });
      });
  };

  scheduleBpSearch = (query) => {
    if (this.bpSearchTimer) {
      clearTimeout(this.bpSearchTimer);
    }
    const trimmed = (query || '').trim();
    if (!shouldRunBpSearch(trimmed)) {
      if (this.bpSearchCancelSource) {
        this.bpSearchCancelSource.cancel('Navbar search cleared');
        this.bpSearchCancelSource = null;
      }
      this.setState({ bpOverlayOpen: false, bpResults: [], bpLoading: false, bpMessage: '' });
      return;
    }
    this.bpSearchTimer = setTimeout(() => {
      this.searchBusinessPartners(trimmed);
    }, bpSearchDelay(trimmed));
  };

  handleInputChange = (e) => {
    const query = e.target.value;
    const results = this.searchNav(query);
    const hasNav = results.length > 0;
    this.setState({
      query,
      results,
      open: query.trim().length > 0 && hasNav,
      activeIndex: 0,
      bpOverlayOpen: false,
      bpResults: [],
      bpMessage: '',
      bpLoading: false
    });
    if (!hasNav) {
      this.scheduleBpSearch(query);
    } else if (this.bpSearchTimer) {
      clearTimeout(this.bpSearchTimer);
    }
  };

  handleKeyDown = (e) => {
    const { results, activeIndex, open, bpOverlayOpen, query } = this.state;
    if (e.key === 'Enter') {
      const trimmed = (query || '').trim();
      if (canSubmitBpSearch(trimmed)) {
        e.preventDefault();
        if (this.bpSearchTimer) {
          clearTimeout(this.bpSearchTimer);
        }
        this.searchBusinessPartners(trimmed);
        return;
      }
    }
    if (bpOverlayOpen) {
      return;
    }
    if (!open || results.length === 0) {
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.setState({ activeIndex: (activeIndex + 1) % results.length });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.setState({ activeIndex: (activeIndex - 1 + results.length) % results.length });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      this.navigateTo(results[activeIndex]);
    } else if (e.key === 'Escape') {
      this.setState({ open: false });
    }
  };

  handleFocus = () => {
    const results = this.searchNav(this.state.query);
    if (this.state.query.trim() && results.length > 0) {
      this.setState({ results, open: true });
    }
  };

  renderMeta(item) {
    if (item.kind === 'api') {
      const method = item.apiMethod || 'GET';
      return [item.menu, item.category, method, item.apiPath].filter(Boolean).join(' · ');
    }
    const parts = [item.menu, item.category];
    if (item.phpFile) {
      parts.push(item.phpFile);
    } else if (item.route) {
      parts.push(item.route);
    }
    return parts.filter(Boolean).join(' · ');
  }

  renderBpRow(bp) {
    const bpId = bp.businessPartnerId;
    const meta = [
      'BP ' + bpId,
      bp.phone || '',
      bp.colonyName || '',
      bp.applicationNo ? 'App ' + bp.applicationNo : ''
    ].filter(Boolean).join(' · ');
    return (
      <div key={'bp-' + bpId} className="crm-bp-overlay-row">
        <button
          type="button"
          className="crm-bp-overlay-item"
          onClick={() => this.openPassbookOverlay(bpId)}
        >
          <span className="crm-bp-overlay-title">{bp.name || 'Business Partner'}</span>
          <span className="crm-bp-overlay-meta">{meta}</span>
        </button>
        <Button
          variant="info"
          className="crm-bp-overlay-action-btn"
          onClick={() => this.openActionList(bp)}
        >
          Action
        </Button>
      </div>
    );
  }

  renderBpOverlay() {
    const { bpOverlayOpen, bpLoading, bpResults, bpMessage } = this.state;
    if (!bpOverlayOpen) {
      return null;
    }
    return (
      <div className="crm-bp-overlay" role="dialog" aria-modal="true" aria-label="Business partner search results">
        <div className="crm-bp-overlay-backdrop" onMouseDown={this.closeBpOverlay} />
        <div className="crm-bp-overlay-panel">
          <div className="crm-bp-overlay-header">
            <h5 className="crm-bp-overlay-heading">Business Partner Search</h5>
            <button type="button" className="crm-bp-overlay-close" onClick={this.closeBpOverlay} aria-label="Close">
              <i className="mdi mdi-close"></i>
            </button>
          </div>
          <div className="crm-bp-overlay-body">
            {bpLoading && <div className="crm-bp-overlay-status">Searching business partners…</div>}
            {!bpLoading && bpMessage && <div className="crm-bp-overlay-status">{bpMessage}</div>}
            {!bpLoading && bpResults.length > 0 && (
              <div className="crm-bp-overlay-list">
                <div className="crm-bp-overlay-list-hint">
                  {bpResults.length === 1
                    ? '1 match — click row to open passbook, or use Action'
                    : bpResults.length + ' matches — click row to open passbook, or use Action'}
                </div>
                {bpResults.map((bp) => this.renderBpRow(bp))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  renderPassbookOverlay() {
    const { passbookOverlayOpen, passbookOverlaySrc, passbookOverlayTitle } = this.state;
    if (!passbookOverlayOpen || !passbookOverlaySrc) {
      return null;
    }
    return (
      <div className="crm-passbook-overlay" role="dialog" aria-modal="true" aria-label="Business partner passbook">
        <div className="crm-passbook-overlay-backdrop" onMouseDown={this.closePassbookOverlay} />
        <div className="crm-passbook-overlay-panel">
          <div className="crm-passbook-overlay-header">
            <h5 className="crm-passbook-overlay-heading">{passbookOverlayTitle}</h5>
            <button type="button" className="crm-passbook-overlay-close" onClick={this.closePassbookOverlay} aria-label="Close">
              <i className="mdi mdi-close"></i>
            </button>
          </div>
          <div className="crm-passbook-overlay-body">
            <iframe
              className="crm-passbook-overlay-frame"
              title={passbookOverlayTitle}
              src={passbookOverlaySrc}
            />
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { query, results, open, activeIndex } = this.state;

    return (
      <>
        <div className="search-field d-none d-md-block crm-nav-search" ref={(el) => { this.containerRef = el; }}>
          <form className="d-flex align-items-center h-100" onSubmit={(e) => e.preventDefault()}>
            <div className="input-group">
              <div className="input-group-prepend bg-transparent">
                <i className="input-group-text border-0 mdi mdi-magnify"></i>
              </div>
              <input
                type="search"
                className="form-control bg-transparent border-0"
                placeholder="Search pages, BP name, mobile, ID…"
                value={query}
                onChange={this.handleInputChange}
                onKeyDown={this.handleKeyDown}
                onFocus={this.handleFocus}
                aria-label="Search CRM navigation and business partners"
                autoComplete="off"
              />
            </div>
          </form>
          {open && results.length > 0 && (
            <div className="crm-nav-search-results">
              {results.map((item, idx) => (
                <button
                  key={(item.route || item.phpPath || item.title) + '-' + idx}
                  type="button"
                  className={'crm-nav-search-item' + (idx === activeIndex ? ' active' : '')}
                  onMouseEnter={() => this.setState({ activeIndex: idx })}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    this.navigateTo(item);
                  }}
                >
                  <span className="crm-nav-search-title">{item.title}</span>
                  <span className="crm-nav-search-meta">{this.renderMeta(item)}</span>
                </button>
              ))}
            </div>
          )}
          {open && query.trim() && results.length === 0 && !this.state.bpLoading && shouldRunBpSearch(query) && (
            <div className="crm-nav-search-results crm-nav-search-empty">
              {isNumericQuery(query) && query.trim().length < 10
                ? 'Type full BP number or mobile (10 digits), or press Enter after 8+ digits…'
                : 'Press Enter or keep typing to search business partners…'}
            </div>
          )}
        </div>
        {this.renderBpOverlay()}
        {this.renderPassbookOverlay()}
        <Modal
          isOpen={this.state.actionListOpen}
          onRequestClose={this.closeActionList}
          ariaHideApp={false}
          style={bpActionListModalStyle}
        >
          <div className="card">
            <div className="card-body">
              <h5>BP Actions{this.state.actionListBp ? ' — BP ' + this.state.actionListBp.businessPartnerId : ''}</h5>
              <div className="list-group" style={{ marginTop: '10px' }}>
                {BP_ACTION_ITEMS.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className="list-group-item list-group-item-action"
                    onClick={() => this.runBpAction(item)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <div className="text-right" style={{ marginTop: '12px' }}>
                <Button variant="secondary" onClick={this.closeActionList}>Close</Button>
              </div>
            </div>
          </div>
        </Modal>
        <Modal
          isOpen={this.state.actionOverlayOpen}
          onRequestClose={this.closeActionOverlay}
          ariaHideApp={false}
          style={bpActionOverlayModalStyle}
        >
          <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '8px' }}>
            <h5 style={{ margin: 0 }}>{this.state.actionOverlayTitle}</h5>
            <Button variant="secondary" size="sm" onClick={this.closeActionOverlay}>Close</Button>
          </div>
          {this.state.actionOverlayPost && (
            <form
              ref={this.actionFormRef}
              method="POST"
              target="bp-navbar-action-frame"
              action={this.state.actionOverlayPostAction}
              style={{ display: 'none' }}
            >
              <input type="hidden" name={this.state.actionOverlayPostField} value={this.state.actionOverlayBn || ''} />
              <input type="hidden" name="user_id" value={localStorage.getItem('UserId') || ''} />
            </form>
          )}
          <iframe
            name="bp-navbar-action-frame"
            title="BP Action"
            style={{ flex: 1, width: '100%', border: 'none' }}
            src={this.state.actionOverlayPost ? 'about:blank' : this.state.actionOverlayUrl}
          />
        </Modal>
      </>
    );
  }
}

export default withRouter(NavbarSearch);
